import type { SiteAccess, ShootSummary } from '@diana/contracts';
import { ArrowLeft, ArrowUpDown, Monitor } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { SortableList } from '../../components/SortableList.js';
import { Button, CollectionSearch, Toggle } from '../../ui/index.js';
import './ShootsList.css';

const uncategorised = '__uncategorised__';
const portfolioCategories = ['portraits', 'families', 'proposals', 'weddings'];

export function ShootsList({
  shoots,
  onSiteAccessChange,
  onOrderChange,
}: {
  shoots: readonly ShootSummary[];
  onSiteAccessChange(shoot: ShootSummary, status: SiteAccess): Promise<void>;
  onOrderChange(shoots: readonly ShootSummary[]): Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('All');
  const [busy, setBusy] = useState<string | null>(null);
  const [draftOrder, setDraftOrder] = useState<readonly ShootSummary[] | null>(null);
  const [orderBusy, setOrderBusy] = useState(false);
  const [error, setError] = useState('');
  const [announcement, setAnnouncement] = useState('');
  const reordering = draftOrder !== null;
  const order = draftOrder ?? shoots;
  const dirtyOrder =
    draftOrder !== null && draftOrder.some((shoot, index) => shoot.id !== shoots[index]?.id);
  const categories = useMemo(() => {
    const extra = [...new Set(shoots.map((shoot) => shoot.payload.category ?? uncategorised))]
      .filter((item) => !portfolioCategories.includes(item))
      .sort((a, b) => categoryLabel(a).localeCompare(categoryLabel(b)));
    return [...portfolioCategories, ...extra];
  }, [shoots]);
  const matches = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return order.filter(
      (shoot) =>
        (category === 'All' || (shoot.payload.category ?? uncategorised) === category) &&
        (!query ||
          shoot.payload.title.toLocaleLowerCase().includes(query) ||
          shoot.payload.subtitle?.toLocaleLowerCase().includes(query) ||
          shoot.payload.category?.toLocaleLowerCase().includes(query)),
    );
  }, [category, search, order]);

  useEffect(() => {
    if (!dirtyOrder) return;
    const preventUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirtyOrder]);

  return (
    <article className="editor galleries-list">
      <header className="galleries-list__heading">
        {reordering ? (
          <span className="dui-back-link galleries-list__back" role="link" aria-disabled="true">
            <ArrowLeft size={16} aria-hidden="true" /> Back to Site
          </span>
        ) : (
          <Link className="dui-back-link galleries-list__back" to="/site">
            <ArrowLeft size={16} aria-hidden="true" /> Back to Site
          </Link>
        )}
        <h1>Galleries</h1>
        <CollectionSearch
          className="galleries-list__search"
          label="Search galleries"
          placeholder="Title or category"
          value={search}
          disabled={reordering || busy !== null}
          onChange={(event) => setSearch(event.currentTarget.value)}
        />
        <div className="galleries-list__actions">
          {reordering ? (
            <>
              <Button disabled={orderBusy} onClick={cancelOrder}>
                Cancel
              </Button>
              <Button variant="primary" loading={orderBusy} onClick={() => void saveOrder()}>
                Save order
              </Button>
            </>
          ) : (
            <Button
              leadingIcon={ArrowUpDown}
              disabled={busy !== null || matches.length < 2}
              onClick={() => {
                setDraftOrder([...shoots]);
                setError('');
                setAnnouncement('');
              }}
            >
              Change order
            </Button>
          )}
        </div>
      </header>

      <div className="galleries-list__filters" role="group" aria-label="Filter by category">
        {['All', ...categories].map((item) => (
          <button
            type="button"
            key={item}
            aria-pressed={category === item}
            disabled={reordering || busy !== null}
            onClick={() => setCategory(item)}
          >
            {categoryLabel(item)}
          </button>
        ))}
      </div>
      {reordering ? (
        <div className="galleries-list__order-note">
          <ArrowUpDown size={18} aria-hidden="true" />
          <p>
            Drag galleries or use the arrows to change their Portfolio order. Filters stay fixed;
            other galleries keep their positions. Save order to keep your changes. Leaving via other
            navigation discards this local unsaved order.
          </p>
        </div>
      ) : (
        <p className="galleries-list__upload-note">
          <Monitor size={18} aria-hidden="true" /> New galleries are uploaded from the Mac app
        </p>
      )}
      {error ? (
        <p className="field-error galleries-list__error" role="alert">
          {error}
        </p>
      ) : null}
      <p className="sr-only" role="status">
        {announcement}
      </p>
      <div className="galleries-list__grid" aria-busy={orderBusy || undefined}>
        {reordering ? (
          <SortableList
            layout="grid"
            disabled={orderBusy}
            items={matches}
            label={(shoot) => shoot.payload.title}
            onChange={changeOrder}
            render={(shoot) => renderGallery(shoot)}
          />
        ) : (
          matches.map((shoot) => renderGallery(shoot))
        )}
      </div>
      {matches.length === 0 ? (
        <div className="galleries-list__empty">
          <h2>No galleries found</h2>
          <p>Try another search or category filter.</p>
        </div>
      ) : null}
    </article>
  );

  function renderGallery(shoot: ShootSummary) {
    const content = (
      <>
        <span className="gallery-card__image">
          <img src={`/api/v1/admin/assets/${shoot.payload.cover.assetId}/preview`} alt="" />
          {shoot.siteAccess === 'AdminOnly' ? (
            <span className="gallery-card__hidden" title="Hidden in the website draft">
              Hidden
            </span>
          ) : null}
        </span>
        <div className="gallery-card__details">
          <h2>{shoot.payload.title}</h2>
          <span className="gallery-card__subtitle">{shoot.payload.subtitle ?? shoot.route}</span>
          <span className="gallery-card__meta">
            {categoryLabel(shoot.payload.category ?? uncategorised)} · {shoot.photoCount}{' '}
            {shoot.photoCount === 1 ? 'photo' : 'photos'}
          </span>
          <span className="gallery-card__updated">
            Updated{' '}
            {new Intl.DateTimeFormat('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
            }).format(new Date(shoot.updatedAt))}
          </span>
        </div>
      </>
    );
    return (
      <article key={shoot.id} className="gallery-card">
        {reordering ? (
          <div className="gallery-card__main">{content}</div>
        ) : (
          <Link className="gallery-card__main" to={`/shoots/${shoot.id}`}>
            {content}
          </Link>
        )}
        <div className="gallery-card__visibility">
          <Toggle
            label={`${shoot.siteAccess === 'Public' ? 'Hide' : 'Show'} ${shoot.payload.title} on website`}
            hideLabel
            checked={shoot.siteAccess === 'Public'}
            disabled={reordering || busy !== null}
            onCheckedChange={(checked) =>
              void changeSiteAccess(shoot, checked ? 'Public' : 'AdminOnly')
            }
          />
          <span title="Draft access. Changes appear on the website after publishing.">
            On website<span className="sr-only"> in the draft; publish to update the website</span>
          </span>
        </div>
      </article>
    );
  }

  async function changeSiteAccess(shoot: ShootSummary, next: SiteAccess) {
    if (busy !== null || reordering) return;
    setBusy(shoot.id);
    setError('');
    setAnnouncement('');
    try {
      await onSiteAccessChange(shoot, next);
      setAnnouncement(
        `${shoot.payload.title} draft visibility saved. Publish to update the website.`,
      );
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save gallery visibility. Try the switch again.'));
    } finally {
      setBusy(null);
    }
  }

  function changeOrder(nextMatches: ShootSummary[]) {
    if (!draftOrder || orderBusy) return;
    const reordered = [...nextMatches];
    const matchedIds = new Set(matches.map((shoot) => shoot.id));
    setDraftOrder(
      draftOrder
        .map((shoot) => (matchedIds.has(shoot.id) ? reordered.shift()! : shoot))
        .map((shoot, position) => ({ ...shoot, position })),
    );
    setError('');
    setAnnouncement('Order changed locally. Save order to keep your changes.');
  }

  function cancelOrder() {
    if (orderBusy) return;
    setDraftOrder(null);
    setError('');
    setAnnouncement('Order changes cancelled.');
  }

  async function saveOrder() {
    if (!draftOrder || orderBusy) return;
    setOrderBusy(true);
    setError('');
    setAnnouncement('Saving order.');
    try {
      await onOrderChange(draftOrder);
      setDraftOrder(null);
      setAnnouncement('Order saved. Publish to update the website.');
    } catch (cause) {
      setError(errorMessage(cause, 'Could not save gallery order. Try Save order again.'));
      setAnnouncement('Order was not saved. Your local changes are ready to retry.');
    } finally {
      setOrderBusy(false);
    }
  }
}

function errorMessage(error: unknown, fallback: string) {
  if (
    error &&
    typeof error === 'object' &&
    'fields' in error &&
    Array.isArray(error.fields) &&
    typeof error.fields[0]?.message === 'string'
  )
    return error.fields[0].message;
  return error instanceof Error ? error.message : fallback;
}

function categoryLabel(category: string) {
  if (category === uncategorised) return 'Uncategorised';
  return category.replaceAll('-', ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}
